import type { Ntc } from '@/services/ntcs.service';
import { ntcsService } from '@/services/ntcs.service';
import { useCallback, useEffect, useState } from 'react';

interface UseNtcsListResult {
  data: Ntc[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para obtener NTCs de una empresa específica
 */
export function useNtcsByEmpresa(nit: string): UseNtcsListResult {
  const [data, setData] = useState<Ntc[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNtcs = useCallback(async () => {
    if (!nit) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await ntcsService.listByEmpresa(nit);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar las NTCs de la empresa');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar las NTCs de la empresa');
    } finally {
      setIsLoading(false);
    }
  }, [nit]);

  useEffect(() => {
    fetchNtcs();
  }, [fetchNtcs]);
  return { data, isLoading, error, refetch: fetchNtcs };
}
interface UseNtcResult {
  data: Ntc | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook para obtener una NTC específica de una empresa
 */
export function useNtcById(nit: string, ntcId: string, enabled: boolean = true): UseNtcResult {
  const [data, setData] = useState<Ntc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNtc = useCallback(async () => {
    if (!enabled || !nit || !ntcId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await ntcsService.getById(nit, ntcId);
      if (response.success) {
        setData(response.data);
      } else {
        setError('Error al cargar la NTC');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la NTC');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, nit, ntcId]);

  useEffect(() => {
    fetchNtc();
  }, [fetchNtc]);

  return { data, isLoading, error, refetch: fetchNtc };
}
